import { createFileRoute } from "@tanstack/react-router";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { VillageApplicationForm } from "@/components/village-application-form";
import { CountdownTimer } from "@/components/countdown-timer";

const JOIN_URL = "https://tablesandbridges.com/village#apply";
const CANONICAL = "https://tablesandbridges.com/village";

import founderAsset from "@/assets/carlai-founder.jpg.asset.json";
import panelAsset from "@/assets/carlai-panel.jpg.asset.json";
import busAsset from "@/assets/group-bus.webp.asset.json";

const HERO_IMG =
  "https://d2xsxph8kpxj0f.cloudfront.net/310519663295640728/HCVakMQnCBpikMsFT2dBLJ/blocc-hero-officer-4eZ4fnrgXWg4DaxGZUfVJb.webp";
const FOUNDER_IMG = founderAsset.url;
const TRAINING_IMG = panelAsset.url;
const PEER_IMG =
  "https://d2xsxph8kpxj0f.cloudfront.net/310519663295640728/HCVakMQnCBpikMsFT2dBLJ/blocc-peer-session-zoom-nZcLM4fdRSxTQF98cW3sDw.webp";
const BUS_IMG = busAsset.url;

export const Route = createFileRoute("/village")({
  head: () => ({
    meta: [
      { title: "The Village - Community for CE Officers" },
      {
        name: "description",
        content:
          "Professional community for community engagement officers. Live trainings, peer sessions, CE Coach AI, and a national network. $75/year - LECEN members save 30%.",
      },
      { property: "og:title", content: "The Village - Stop Figuring It Out Alone." },
      {
        property: "og:description",
        content:
          "The professional home for community engagement officers. Trainings, peer sessions, resources, and the CE Coach AI - built by a practitioner, for practitioners.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL },
      { property: "og:image", content: HERO_IMG },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "The Village - Stop Figuring It Out Alone." },
      {
        name: "twitter:description",
        content:
          "The professional home for community engagement officers. Trainings, peer sessions, resources, CE Coach AI.",
      },
      { name: "twitter:image", content: HERO_IMG },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          name: "The Village",
          description:
            "Professional community, training, and peer network for community engagement officers. $75/year - LECEN members save 30%.",
          brand: { "@type": "Brand", name: "Tables and Bridges" },
          url: CANONICAL,
          image: HERO_IMG,
          offers: [
            {
              "@type": "Offer",
              name: "The Village Membership",
              price: "75.00",
              priceCurrency: "USD",
              category: "Annual subscription",
              availability: "https://schema.org/InStock",
              url: JOIN_URL,
            },
          ],
        }),
      },
    ],
  }),
  component: VillagePage,
});

function JoinBtn({
  children = "Join the Neighborhood",
  variant = "primary",
  className = "",
}: {
  children?: React.ReactNode;
  variant?: "primary" | "outline" | "navy";
  className?: string;
}) {
  const cls =
    variant === "outline" ? "btn-outline" : variant === "navy" ? "btn-navy" : "btn-primary";
  return (
    <a href="#apply" className={`${cls} ${className}`}>
      {children}
    </a>
  );
}

const pillars = ["BUILD", "PROVE", "LEAD", "CONNECT"];

const stats = [
  { n: "50", label: "States Represented in the Network" },
  { n: "2", label: "LIVE TRAINING SESSION / MONTH" },
  { n: "1", label: "Live Peer Sessions / Month" },
  { n: "24/7", label: "CE Coach - Always On" },
  { n: "1", label: "Space Built to Strengthen Your Impact" },
];

const painPoints = [
  "You are building a community engagement program from scratch with no budget, no template, and no one to call.",
  "You sit in roll call and nobody asks about your work. You sit in budget meetings and nobody funds it.",
  "You lost half your staff and still have the same community expectations to deliver.",
  "You go to the conference. You come back fired up. Then you go back to figuring it out alone.",
  "You know exactly what real engagement looks like but you have no system to prove it is working.",
  "It is 10pm and you are still thinking about a conversation from this morning that nobody else would understand.",
];

const features = [
  {
    eyebrow: "The Table Training Series",
    title: "Real Training. Every Month.",
    body: "Live training sessions every month - skills-based, proven frameworks, and guest experts speaking directly to the challenges you face every day. No fluff training built to help you build stronger programs, increase community participation, and walk into every meeting with confidence.",
    img: TRAINING_IMG,
    alt: "Carlai Moore leading a panel discussion at a training session",
  },
  {
    eyebrow: "National Peer Network",
    title: "Find Your People on Every Block.",
    body: "A national network of community engagement officers organized by state and role. Find exactly who you need when you need them. Share what is working. Strengthen partnerships across departments. Ask what you cannot ask anywhere else.",
    img: BUS_IMG,
    alt: "Group of officers on a bus building community energy",
  },
  {
    eyebrow: "Live Peer Sessions",
    title: "Bring Your Real Problem. Leave With a Real Solution.",
    body: "Twice a month we get on a live call. You bring what is actually happening in your work. The room helps you work through it with frameworks, with peers, and with someone who has been in that room before. Every session is recorded.",
    img: PEER_IMG,
    alt: "Officers from departments across the country on a live The Village peer session call",
  },
];

const pillarCards = [
  {
    icon: "🏗",
    name: "BUILD",
    body: "For the officer launching a program from scratch. No budget. No template. No problem. We build it together.",
  },
  {
    icon: "📊",
    name: "PROVE",
    body: "For the officer who needs to walk into the next budget meeting with data that defends their unit. We help you build the receipts.",
  },
  {
    icon: "🧭",
    name: "LEAD",
    body: "For the officer holding their unit together through short staff, high expectations, and impossible circumstances. We help you lead through it.",
  },
  {
    icon: "🤝",
    name: "CONNECT",
    body: "For the officer who just needs someone who gets it. A peer. A practitioner. Someone who has already solved the problem you are stuck on.",
  },
];

const without = [
  "Googling for resources and finding nothing built for CE work",
  "Sitting in a general training that has nothing to do with your role",
  "Figuring out programs alone - no one to call when something falls flat",
  "Feeling like the only one in your department who gets it",
  "Watching your motivation fade because there's no community behind you",
  "Starting over every year with no institutional knowledge to pull from",
];

const inside = [
  "Resources built to help you increase community participation and prove your impact",
  "Monthly training sessions led by people doing this work right now",
  "Peers from departments across the country you can call on",
  "A coach in Carlai Moore who has been in this space for years",
  "Accountability that helps you build stronger programs all year",
  "Institutional knowledge that grows with you - session after session",
];

const faqs = [
  {
    q: "Who is the Village for?",
    a: "Community engagement officers, community liaisons, and law enforcement professionals whose primary role is building relationships between their department and the public. If your job is to show up in the community and you are doing it largely alone, this is for you.",
  },
  {
    q: "Do I need department approval to join?",
    a: "No. Sponsorship is $75/year - less than most professional development line items. LECEN members save 30% ($52.50/year). Many officers join on their own.",
  },
  {
    q: "What does my $75/year sponsorship support?",
    a: "Your sponsorship keeps the Neighborhood running - the training sessions, peer network, resource library, and coaching that show up for community engagement officers all year. It also funds the platform, tools, and time it takes to build resources specifically for CE work that no one else is building.",
  },
  {
    q: "What is LECEN and how do I get the 30% discount?",
    a: "LECEN is the Law Enforcement Community Engagement Network. LECEN members receive a 30% discount code applied at checkout, bringing sponsorship to $52.50/year. If you are a LECEN member and need your code, email info@tablesandbridges.com.",
  },
  {
    q: "How does sponsorship help other officers?",
    a: "5% of every sponsorship helps fund professional development opportunities for community engagement officers who do not have the budget or department backing to access training on their own. When you sponsor yourself, you also help open a door for the officer on the next block.",
  },
  {
    q: "Why is it called sponsorship instead of membership?",
    a: "Because you are sponsoring the work - yours and the field's. CE officers are often the only one in their department doing this job. Sponsoring the Neighborhood says this work matters and deserves real investment.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Yes. Sponsorship is billed annually and can be canceled anytime before the next renewal.",
  },
  {
    q: "What if I am not sure this is right for me?",
    a: "Reach out. Email info@tablesandbridges.com and tell us where you are in your work. We will tell you honestly whether the Neighborhood is the right fit right now.",
  },
];

function VillagePage() {
  return (
    <div className="bg-navy text-foreground">
      <CountdownTimer />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 pt-14 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-green" />
              <span className="eyebrow">Presented by Tables and Bridges</span>
            </div>
            <h1 className="font-display uppercase leading-[0.92] text-[44px] sm:text-6xl lg:text-[88px] tracking-tight">
              Stop Figuring It{" "}<br />Out Alone.{" "}
              <span className="block text-gold mt-3">The Village.</span>
            </h1>
            <p className="mt-8 max-w-xl text-base sm:text-lg text-foreground/80 leading-relaxed">
              The professional community, training, and peer network built specifically for
              community engagement officers. Wherever you are in the work, you belong here.
            </p>

            <div className="mt-7 flex flex-wrap gap-2">
              {pillars.map((p) => (
                <span key={p} className="tag-pill">{p}</span>
              ))}
            </div>

            <div className="mt-9 flex flex-col sm:flex-row sm:items-center gap-5">
              <JoinBtn />
              <p className="text-xs text-foreground/55 italic max-w-xs">
                Carlai personally reviews every application within 24 hours.
              </p>
            </div>

            <p className="mt-8 text-sm text-foreground/70">
              <span className="text-foreground font-semibold">$75/year</span>
              <span className="ml-2 inline-block px-2 py-0.5 rounded-sm bg-gold/15 text-gold text-xs font-semibold">
                🏅 LECEN members save 30%
              </span>
            </p>
          </div>

          <div className="lg:col-span-5">
            <div className="relative">
              <div
                className="absolute -inset-3 sm:-inset-4 border-2 border-green rounded-sm translate-x-3 translate-y-3"
                aria-hidden
              />
              <img
                src={HERO_IMG}
                alt="Community engagement officer standing in a neighborhood at golden hour"
                className="relative w-full h-auto rounded-sm object-cover shadow-2xl"
                fetchPriority="high"
              />
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-green bg-green">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10 grid grid-cols-2 md:grid-cols-5 gap-y-8 gap-x-4">
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              <div className="font-display font-bold text-4xl sm:text-5xl text-warm-white leading-none">
                {s.n}
              </div>
              <div className="mt-2 text-[11px] sm:text-xs font-semibold tracking-[0.16em] uppercase text-warm-white max-w-[14ch] mx-auto">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* INFRASTRUCTURE */}
      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="eyebrow mb-5">You Are Not the Problem</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-5xl leading-[0.95] break-words">
              The Infrastructure Was Never Built For You.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-foreground/85 text-base sm:text-lg leading-relaxed">
            <p>
              You chose this work because you believe it matters. You showed up ready to build
              real relationships between law enforcement and the communities you serve.
            </p>
            <p>
              But nobody gave you a playbook. No framework for what to do between the events.
              No peers who actually get it. No system for proving the work is making a
              difference before the next budget meeting.
            </p>
            <p className="text-foreground font-semibold">
              That is not a you problem. That is an infrastructure problem.
            </p>
            <p>
              That ends here. The Village is the infrastructure that was never
              built for you - until now. Presented by Tables and Bridges.
            </p>
            <div className="pt-4"><JoinBtn /></div>
          </div>
        </div>
      </section>

      {/* SOUND FAMILIAR */}
      <section className="py-20 lg:py-24 bg-[color:var(--secondary)]/40 border-y border-border/40">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <p className="eyebrow mb-4">This Is for You If</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl">Sound Familiar?</h2>
          </div>
          <ul className="grid md:grid-cols-2 gap-5">
            {painPoints.map((p, i) => (
              <li
                key={i}
                className="flex gap-4 p-6 rounded-sm border border-border/60 bg-navy/60"
              >
                <span className="flex-none mt-1 inline-flex h-6 w-6 items-center justify-center rounded-full border border-gold text-gold text-xs font-bold">
                  {i + 1}
                </span>
                <p className="text-foreground/85 leading-relaxed">{p}</p>
              </li>
            ))}
          </ul>
          <div className="text-center mt-12"><JoinBtn /></div>
        </div>
      </section>

      {/* FOUNDER */}
      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="relative">
              <div
                className="absolute -inset-3 border-2 border-green rounded-sm -translate-x-3 translate-y-3"
                aria-hidden
              />
              <img
                src={FOUNDER_IMG}
                alt="Carlai Moore, Founder of Tables and Bridges and The Village"
                className="relative w-full h-auto rounded-sm object-cover"
                loading="lazy"
              />
            </div>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <p className="eyebrow mb-4">Built by Someone Who Is in It</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
              I Am Not a Consultant. <span className="text-gold">I Am a Practitioner.</span>
            </h2>
            <div className="mt-7 space-y-4 text-foreground/85 text-base sm:text-lg leading-relaxed">
              <p>
                I am Carlai Moore - an active law enforcement sergeant, community engagement
                practitioner, and founder of Tables and Bridges.
              </p>
              <p>
                <span className="font-semibold text-foreground">Tables and Bridges</span> is
                the umbrella brand - the professional home for community engagement officers.
                The Village is the community product inside it. The Village newsletter,
                podcast, speaking, and consulting all live under Tables and Bridges. The
                Neighborhood is where the daily work happens.
              </p>
              <p>
                I built the Village because I was sitting in the same gap you are
                in right now. Doing the work. Having no framework. No peers. No way to prove
                it was making a difference.
              </p>
              <p>
                I looked for the community that would help me build, prove, lead, and connect.
                It did not exist. So I built it.
              </p>
            </div>
            <p className="mt-7 text-xs tracking-[0.18em] uppercase font-semibold text-foreground/60">
              Founder, Tables and Bridges <span className="text-gold">|</span> Law Enforcement Sergeant <span className="text-gold">|</span> Community Engagement Practitioner
            </p>
            <div className="mt-7"><JoinBtn /></div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-20 lg:py-24 bg-[color:var(--secondary)]/40 border-y border-border/40">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="eyebrow mb-4">What Happens Inside the Neighborhood</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
              Everything You Were Never Given.<br />
              <span className="text-gold">All In One Place.</span>
            </h2>
            <p className="mt-6 text-foreground/75 text-lg">
              Five tools. One community. Built specifically for the work you do every day.
            </p>
          </div>

          <div className="space-y-20">
            {features.map((f, i) => (
              <div
                key={f.title}
                className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center"
              >
                <div
                  className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-2" : ""}`}
                >
                  <div className="relative">
                    <div
                      className={`absolute -inset-3 border-2 border-green rounded-sm ${i % 2 === 1 ? "-translate-x-3" : "translate-x-3"} translate-y-3`}
                      aria-hidden
                    />
                    <img
                      src={f.img}
                      alt={f.alt}
                      className="relative w-full h-auto rounded-sm object-cover"
                      loading="lazy"
                    />
                  </div>
                </div>
                <div className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                  <p className="eyebrow mb-3">{f.eyebrow}</p>
                  <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1]">
                    {f.title}
                  </h3>
                  <p className="mt-5 text-foreground/85 leading-relaxed text-base sm:text-lg">
                    {f.body}
                  </p>
                  <div className="mt-7"><JoinBtn variant="outline">Join the Neighborhood</JoinBtn></div>
                </div>
              </div>
            ))}

            {/* CE Coach feature with chat mock */}
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
              <div className="lg:col-span-6">
                <div className="rounded-sm border border-green/40 bg-navy p-6 sm:p-8 shadow-2xl">
                  <div className="flex items-center gap-3 pb-4 border-b border-border/50">
                    <div className="h-10 w-10 rounded-full bg-green text-warm-white font-display text-lg flex items-center justify-center">
                      CE
                    </div>
                    <div>
                      <div className="font-display text-lg uppercase">CE Coach</div>
                      <div className="text-[11px] tracking-[0.18em] uppercase text-foreground/60">
                        AI-Powered · Always On
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 space-y-4 text-sm sm:text-base">
                    <div className="ml-auto max-w-[85%] rounded-sm bg-green text-warm-white px-4 py-3 font-medium">
                      How do I get patrol officers to actually show up to my events?
                    </div>
                    <div className="max-w-[85%] rounded-sm bg-[color:var(--secondary)] text-foreground/90 px-4 py-3">
                      Let's start with what's blocking them. Is it scheduling, buy-in from
                      supervisors, or something else?
                    </div>
                    <div className="ml-auto max-w-[85%] rounded-sm bg-green text-warm-white px-4 py-3 font-medium">
                      Mostly buy-in. They don't see it as their job.
                    </div>
                    <div className="max-w-[85%] rounded-sm bg-[color:var(--secondary)] text-foreground/90 px-4 py-3">
                      That's a framing issue we can fix. Here are three approaches that have
                      worked in similar departments…
                    </div>
                  </div>
                  <p className="mt-5 text-[11px] tracking-[0.18em] uppercase text-foreground/55">
                    Available to Every Member
                  </p>
                </div>
              </div>
              <div className="lg:col-span-6">
                <p className="eyebrow mb-3">CE Coach</p>
                <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1]">
                  The AI Coach Every Community Engagement Officer Deserves.
                </h3>
                <p className="mt-5 text-foreground/85 leading-relaxed text-base sm:text-lg">
                  CE Coach is an AI-powered platform built exclusively for community engagement
                  officers and liaisons. Ask anything. Get answers built around your real work.
                  Available to every member. Always on. Always ready.
                </p>
                <div className="mt-7"><JoinBtn variant="outline" /></div>
              </div>
            </div>

            {/* Resource Library - text only block */}
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
              <div className="lg:col-span-5">
                <p className="eyebrow mb-3">Resource Library</p>
                <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1]">
                  Built For Every Budget. <span className="text-gold">Including Zero.</span>
                </h3>
              </div>
              <div className="lg:col-span-7">
                <p className="text-foreground/85 leading-relaxed text-base sm:text-lg">
                  A growing library of templates, frameworks, guides, and tools built around
                  what community engagement officers actually need. Grant finders. Event
                  planning templates. Budget justification guides. Impact measurement tools to
                  help you measure and prove your work is making a difference. Everything
                  organized and ready to use immediately.
                </p>
                <div className="mt-7"><JoinBtn /></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PULL QUOTE */}
      <section className="py-20 lg:py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 text-center">
          <blockquote className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1]">
            <span className="text-gold">"</span>You don't need another training. You need a
            space for the doers, the people who understand how you feel, and what you walked
            into.<span className="text-gold">"</span>
          </blockquote>
          <p className="mt-6 text-xs tracking-[0.22em] uppercase font-semibold text-foreground/65">
            Carlai Moore - Sgt. &amp; Founder
          </p>
        </div>
      </section>

      {/* SPONSORSHIP */}
      <section className="py-16 lg:py-20 bg-green text-warm-white">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <p className="text-xs font-bold tracking-[0.22em] uppercase text-gold mb-3">
              THE VILLAGE SPONSORSHIP
            </p>
            <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1]">
              Your Membership Sponsors an Officer Who Couldn't Get Here on Their Own.
            </h3>
          </div>
          <div className="lg:col-span-5 space-y-4 text-warm-white/90">
            <p>
              5% of every membership helps fund{" "}
              <span className="font-bold">professional development opportunities</span> for
              community engagement officers who don't have department support.
            </p>
            <p>
              When you join, you invest in yourself and in the officer on the next block who
              hasn't had access yet.
            </p>
          </div>

        </div>
      </section>

      {/* PILLARS */}
      <section className="py-20 lg:py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <p className="eyebrow mb-4">Your Journey. Your Pillar.</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
              Wherever You Are.<br />
              <span className="text-gold">THERE IS A PLACE FOR YOU IN THE VILLAGE.</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {pillarCards.map((p) => (
              <div
                key={p.name}
                className="p-7 rounded-sm border border-border/60 bg-[color:var(--secondary)]/50 hover:border-green/70 transition-colors"
              >
                <div className="text-4xl mb-4" aria-hidden>{p.icon}</div>
                <h3 className="font-display text-2xl text-gold mb-3">{p.name}</h3>
                <p className="text-foreground/80 text-sm leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12"><JoinBtn /></div>
        </div>
      </section>

      {/* COMPARISON */}
      <section className="py-20 lg:py-24 bg-[color:var(--secondary)]/40 border-y border-border/40">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <p className="eyebrow mb-4">The Difference</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl">
              Without It vs. <span className="text-gold">Inside It</span>
            </h2>
            <p className="mt-5 text-foreground/75 text-lg">
              Community engagement officers who go it alone share the same story. Here is what
              changes when you stop.
            </p>
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="p-8 rounded-sm border border-border/60 bg-navy/60">
              <h3 className="font-display text-xl uppercase text-foreground/70 mb-6 tracking-wide">
                Without the Neighborhood
              </h3>
              <ul className="space-y-4">
                {without.map((w) => (
                  <li key={w} className="flex gap-3 text-foreground/75">
                    <span className="flex-none mt-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-destructive/20 text-destructive text-xs font-bold">
                      ✕
                    </span>
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-8 rounded-sm border-2 border-green bg-navy">
              <h3 className="font-display text-xl uppercase text-gold mb-6 tracking-wide">
                Inside the Village
              </h3>
              <ul className="space-y-4">
                {inside.map((w) => (
                  <li key={w} className="flex gap-3 text-foreground/90">
                    <span className="flex-none mt-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-green text-warm-white text-xs font-bold">
                      ✓
                    </span>
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="text-center mt-12"><JoinBtn /></div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-20 lg:py-28">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <p className="eyebrow mb-4">One Membership. One Price.</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl">
              No Surprises. No Contracts. <span className="text-gold">Just Access.</span>
            </h2>
          </div>

          <div className="max-w-md mx-auto">
            <PriceCard
              name="The Village Membership"
              price="$75"
              cadence="/year"
              highlighted
              note="$75/year. LECEN members save 30% ($52.50/year)."
              features={[
                "Full community access",
                "The Table Training Series",
                "National peer network",
                "Resource library",
                "CE Coach access",
                "Live peer sessions",
                "Cancel anytime before next renewal",
              ]}
            />
            <div className="mt-6 rounded-sm border border-green/50 bg-green/10 px-5 py-4 text-center">
              <p className="font-display uppercase tracking-wide text-gold text-sm">
                🏅 LECEN Members Save 30% - $52.50/year
              </p>
              <p className="mt-2 text-xs text-foreground/75 leading-relaxed">
                Apply your LECEN discount code after approval.
              </p>
            </div>
          </div>

          <p className="mt-10 text-center text-sm text-foreground/70 max-w-2xl mx-auto">
            The most affordable ongoing professional development built specifically for
            community engagement officers. Runs all year.
          </p>
        </div>
      </section>

      {/* APPLICATION */}
      <section id="apply" className="py-20 lg:py-28 bg-[color:var(--secondary)]/20 border-y border-border/40">
        <div className="mx-auto max-w-2xl px-4 sm:px-6">
          <div className="text-center mb-10">
            <p className="eyebrow mb-4">Apply to Join</p>
            <h2 className="font-display text-4xl sm:text-5xl">
              Apply to Join <span className="text-gold">the Village.</span>
            </h2>
            <p className="mt-4 text-foreground/75 leading-relaxed">
              Takes less than 3 minutes - name, department, and why CE work matters to you.
            </p>
          </div>
          <div className="rounded-sm border border-border/60 bg-navy/60 p-6 sm:p-10">
            <VillageApplicationForm />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 lg:py-24 bg-[color:var(--secondary)]/40 border-y border-border/40">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <div className="text-center mb-12">
            <p className="eyebrow mb-4">Frequently Asked Questions</p>
            <h2 className="font-display text-4xl sm:text-5xl">
              Questions? <span className="text-gold">We Have Answers.</span>
            </h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((f, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="border border-border/60 rounded-sm bg-navy/60 px-5"
              >
                <AccordionTrigger className="text-left font-display text-lg uppercase tracking-wide hover:text-gold hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-foreground/80 leading-relaxed text-base">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <p className="mt-10 text-center text-foreground/70">
            Still have questions? Email us directly.{" "}
            <a href="mailto:info@tablesandbridges.com" className="text-gold underline underline-offset-4">
              info@tablesandbridges.com
            </a>
          </p>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 lg:py-32 text-center relative overflow-hidden">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          <h2 className="font-display text-4xl sm:text-6xl lg:text-7xl leading-[0.95]">
            Wherever You Are in the Work.<br />
            <span className="text-gold">You Belong Here.</span>
          </h2>
          <p className="mt-6 text-lg tracking-[0.18em] uppercase font-semibold text-foreground/70">
            Build · Prove · Lead · Connect
          </p>
          <p className="mt-8 max-w-2xl mx-auto text-foreground/85 text-base sm:text-lg leading-relaxed">
            The Village is open.{" "}
            <span className="font-semibold text-foreground">$75/year - LECEN members save 30%.</span>{" "}
            Join now and bring your network with you.
          </p>
          <div className="mt-10"><JoinBtn /></div>
          <p className="mt-10 text-xs tracking-[0.18em] uppercase text-foreground/55">
            Part of the Tables and Bridges ecosystem - the professional home for community
            engagement officers.
          </p>
        </div>
      </section>
    </div>
  );
}

function PriceCard({
  name,
  price,
  cadence,
  features,
  highlighted = false,
  badge,
  note,
  cta = "Join the Neighborhood",
}: {
  name: string;
  price: string;
  cadence: string;
  features: string[];
  highlighted?: boolean;
  badge?: string;
  note?: string;
  cta?: string;
}) {
  return (
    <div
      className={`relative flex flex-col rounded-sm p-8 border ${
        highlighted
          ? "border-green border-2 bg-navy shadow-2xl lg:scale-[1.03]"
          : "border-border/60 bg-[color:var(--secondary)]/40"
      }`}
    >
      {badge && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-green text-warm-white text-[10px] tracking-[0.22em] uppercase font-bold px-3 py-1 rounded-sm">
          {badge}
        </div>
      )}
      <h3 className="font-display text-2xl uppercase tracking-wide">{name}</h3>
      <div className="mt-4 flex items-baseline gap-1">
        <span className="font-display text-5xl text-gold">{price}</span>
        <span className="text-sm text-foreground/65">{cadence}</span>
      </div>
      {note && <p className="mt-3 text-sm text-foreground/70 leading-relaxed">{note}</p>}
      <ul className="mt-6 space-y-3 flex-1">
        {features.map((f) => (
          <li key={f} className="flex gap-3 text-sm text-foreground/85">
            <span className="flex-none mt-1 inline-flex h-4 w-4 items-center justify-center rounded-full bg-green text-warm-white text-[10px] font-bold">
              ✓
            </span>
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <div className="mt-7">
        <JoinBtn variant={highlighted ? "primary" : "outline"} className="w-full">
          {cta}
        </JoinBtn>
      </div>
    </div>
  );
}
